import { useState, useRef } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Search, Building2, MapPin, Plus, X } from "lucide-react"
import { PublicShell } from "@/components/PublicShell"

type PrimaryImage = { url: string; alt_text: string }

type VenueRow = {
  id: number
  name: string
  slug: string
  city: string
  state: string
  region_name: string | null
  gigs_count: number
  primary_image?: PrimaryImage | null
}

type Region = { id: number; name: string }

type Props = {
  venues: VenueRow[]
  total_count: number
  search: string | null
  state: string | null
  region_id: number | null
  regions: Region[]
  can_create: boolean
}

const AU_STATES = [ "ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA" ]

function VenueCard({ venue }: { venue: VenueRow }) {
  return (
    <Link
      href={`/venues/${venue.slug}`}
      className="group block w-full text-left bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl overflow-hidden hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all duration-150 no-underline"
    >
      <div className="aspect-square bg-stone-100 dark:bg-stone-800 relative overflow-hidden">
        {venue.primary_image ? (
          <img
            src={venue.primary_image.url}
            alt={venue.primary_image.alt_text}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Building2 className="w-12 h-12 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />
          </div>
        )}
      </div>

      <div className="p-3">
        <h3
          className="text-sm font-semibold text-stone-900 dark:text-white mb-1 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug"
          style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
        >
          {venue.name}
        </h3>
        <div className="flex items-center gap-1 text-xs text-stone-500 dark:text-stone-400" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
          <MapPin className="w-3 h-3 shrink-0" />
          <span>{venue.city}, {venue.state}</span>
        </div>
        {venue.region_name && (
          <p className="text-xs text-stone-400 dark:text-stone-500 mt-0.5" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
            {venue.region_name}
          </p>
        )}
        {venue.gigs_count > 0 && (
          <p className="text-xs text-stone-400 dark:text-stone-500 mt-1" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
            {venue.gigs_count} gig{venue.gigs_count !== 1 ? "s" : ""}
          </p>
        )}
      </div>
    </Link>
  )
}

export default function VenuesIndex({ venues, total_count, search, state, region_id, regions, can_create }: Props) {
  const [ query, setQuery ] = useState(search ?? "")
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  function navigate(overrides: Record<string, string | null | undefined>) {
    const params: Record<string, string> = {}
    const q = "search" in overrides ? overrides.search : query
    const s = "state" in overrides ? overrides.state : state
    const r = "region_id" in overrides ? overrides.region_id : region_id?.toString()
    if (q) params.search = q
    if (s) params.state = s
    if (r) params.region_id = r
    router.get("/venues", params, { preserveState: true, replace: true })
  }

  function handleQueryChange(value: string) {
    setQuery(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => navigate({ search: value || null }), 300)
  }

  const isFiltered = !!(search || state || region_id)

  return (
    <PublicShell>
      <Head title="Venues">
        <meta name="description" content="Australian live music venues — pubs, clubs, theatres, and arenas where Australian music history was made." />
        <meta property="og:title" content="Venues" />
        <meta property="og:description" content="Australian live music venues — pubs, clubs, theatres, and arenas where Australian music history was made." />
      </Head>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-1" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
              Venues
            </h1>
            <p className="text-stone-500 dark:text-stone-400 text-sm">
              {total_count.toLocaleString()} venues
            </p>
          </div>
          {can_create && (
            <Link
              href="/venues/new"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors no-underline shrink-0"
            >
              <Plus className="w-4 h-4" />
              New venue
            </Link>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              placeholder="Search venues…"
              className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-stone-900 dark:text-stone-100 placeholder-stone-400"
            />
          </div>

          <select
            value={state ?? ""}
            onChange={(e) => navigate({ state: e.target.value || null })}
            className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg text-sm text-stone-700 dark:text-stone-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <option value="">All states</option>
            {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>

          {regions.length > 0 && (
            <select
              value={region_id ?? ""}
              onChange={(e) => navigate({ region_id: e.target.value || null })}
              className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg text-sm text-stone-700 dark:text-stone-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">All regions</option>
              {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          )}

          {isFiltered && (
            <button
              onClick={() => { setQuery(""); navigate({ search: null, state: null, region_id: null }) }}
              className="flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200"
            >
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>

        {/* Filtered count */}
        {isFiltered && (
          <p className="text-xs text-stone-500 dark:text-stone-400 mb-4">
            {venues.length} result{venues.length !== 1 ? "s" : ""}
            {venues.length === 100 && " (first 100)"}
          </p>
        )}

        {/* Grid */}
        {venues.length === 0 ? (
          <div className="py-16 text-center">
            <Building2 className="w-6 h-6 text-stone-300 dark:text-stone-700 mx-auto mb-3" />
            <p className="text-stone-500 dark:text-stone-400">No venues found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {venues.map((venue) => <VenueCard key={venue.id} venue={venue} />)}
          </div>
        )}
      </div>
    </PublicShell>
  )
}
